import type { UAApps } from '@unifyapps/defs/types/apps';
import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import type { InfiniteData } from '@unifyapps/network/react-query';
import { produce } from 'immer';
import type { JSONSchema7 } from 'json-schema';
import { get, omit } from 'lodash';
import _get from 'lodash/get';
import _set from 'lodash/set';
import type { RunAndGetSchemaResponse } from '../components/DataSourceWorkflowNode/api';
import type { InfinitePaginationInfo } from '../components/InfiniteDataSourceWorkflowNode/types';
import { RAW_RESPONSE_KEY, TRANSFORMED_RESPONSE_KEY } from '../consts';
import { adaptDataSourceWorkflowNode } from '../utils/adaptDataSourceWorkflowNode';

// a page may omit the data path entirely; coerce non-array values to [] so flatMap
// doesn't leak an undefined element into the merged array
function combinePagesData(pages: { response: unknown }[], dataPath: string): unknown[] {
  return pages.flatMap((page) => {
    const pageData = _get(page, dataPath) as unknown;
    return Array.isArray(pageData) ? (pageData as unknown[]) : [];
  });
}

type AdaptDataSourceResponseParams = {
  isTransformerConfigured: boolean;
  initialData: unknown;
  appName?: UAApps;
  payload: ExecuteWorkflowNodeRequest;
};

type AdaptDataSourceResponseResult = {
  rawData?: unknown;
  data?: unknown;
  outputSchema?: JSONSchema7;
};

/**
 * Adapts the response from the backend to the expected format
 * @param params - The parameters for adapting the response
 * @returns The adapted response with raw data, transformed data, and output schema
 */
export function useAdaptDataSourceResponse({
  isTransformerConfigured,
  initialData,
  payload,
  appName,
}: AdaptDataSourceResponseParams): AdaptDataSourceResponseResult {
  if (!isTransformerConfigured) {
    const adaptedData = adaptDataSourceWorkflowNode(initialData, payload.lookupRequests, appName);
    return {
      rawData: adaptedData,
      data: adaptedData,
    };
  }

  const _initialData = initialData as RunAndGetSchemaResponse | undefined;
  if (!initialData) {
    return {};
  }

  const _initialDataWithoutSchema = omit(_initialData, 'responseSchema');
  const rawResponse = _initialData?.response?.[RAW_RESPONSE_KEY];
  const transformedResponse = _initialData?.response?.[TRANSFORMED_RESPONSE_KEY];

  const transformedOutputSchema = get(
    _initialData?.responseSchema,
    `properties.${TRANSFORMED_RESPONSE_KEY}`,
  );

  const adaptData = (response: unknown) =>
    adaptDataSourceWorkflowNode(
      {
        ..._initialDataWithoutSchema,
        response,
      },
      payload.lookupRequests,
      appName,
    );

  return {
    rawData: adaptData(rawResponse),
    data: adaptData(transformedResponse),
    outputSchema: transformedOutputSchema,
  };
}

type UseAdaptInfiniteDataSourceResponseParams = {
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- TODO: fix this
  initialData: InfiniteData<{ response: unknown } | undefined | unknown> | undefined;
  payload: ExecuteWorkflowNodeRequest;
  isTransformerConfigured: boolean;
  infinitePaginationInfo: InfinitePaginationInfo;
};

type UseAdaptInfiniteDataSourceResponseResult = {
  data?: unknown;
  outputSchema?: JSONSchema7;
};

export function useAdaptInfiniteDataSourceResponse({
  initialData,
  payload,
  isTransformerConfigured,
  infinitePaginationInfo,
}: UseAdaptInfiniteDataSourceResponseParams): UseAdaptInfiniteDataSourceResponseResult {
  if (!isTransformerConfigured) {
    if (!initialData?.pages.length) {
      return {};
    }

    // Adapt each page's data with lookup requests
    const adaptedPages = initialData.pages.map((page) =>
      adaptDataSourceWorkflowNode(page, payload.lookupRequests),
    ) as { response: unknown }[];

    const lastPage = adaptedPages[adaptedPages.length - 1];

    // Start with the last page's data as the base
    const response = produce({ ...lastPage }, (draft) => {
      // Replace the data array with the combined data from all pages
      _set(
        draft,
        infinitePaginationInfo.dataPath,
        combinePagesData(adaptedPages, infinitePaginationInfo.dataPath),
      );
    });

    return {
      data: response,
    };
  }

  const _initialData = initialData as InfiniteData<RunAndGetSchemaResponse | undefined> | undefined;

  if (!_initialData?.pages.length) {
    return {};
  }

  // in infinite data source, we will build the response data by combining the data pill from the transformed data of each page
  // so we need to combine the transformed data of each page
  const combinedTransformedData = _initialData.pages.map((page) => {
    // right now the page looks like this:
    // {
    //   response: {
    //     [TRANSFORMED_RESPONSE_KEY]: unknown,
    //   },
    // }

    // we switch it to look like this:
    // {
    //   response: unknown (this unknown is the value from the [TRANSFORMED_RESPONSE_KEY] of the page)
    // }

    const pageWithoutResponseSchema = omit(page, 'responseSchema');

    return {
      ...pageWithoutResponseSchema,
      response: page?.response?.[TRANSFORMED_RESPONSE_KEY],
    };
  });

  const adaptedPages = combinedTransformedData.map((page) =>
    adaptDataSourceWorkflowNode(page, payload.lookupRequests),
  ) as { response: unknown }[];

  const lastPage = adaptedPages[adaptedPages.length - 1];

  const response = produce({ ...lastPage }, (draft) => {
    _set(
      draft,
      infinitePaginationInfo.dataPath,
      combinePagesData(adaptedPages, infinitePaginationInfo.dataPath),
    );
  });

  const lastInitialPage = _initialData.pages[_initialData.pages.length - 1];
  const outputSchema = get(
    lastInitialPage?.responseSchema,
    `properties.${TRANSFORMED_RESPONSE_KEY}`,
  );

  return {
    data: response,
    outputSchema,
  };
}
